import React, { useCallback, useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import {
    ScanLine,
    ShieldCheck,
    Users,
    Ban,
    Ticket,
    ArrowRight,
    Check,
    BarChart3,
    WifiOff,
    ChevronLeft,
    ChevronRight,
} from "lucide-react";

const FEATURES = [
    {
        icon: ScanLine,
        title: "Scan QR rapide",
        text: "Vos équipes valident les billets en quelques secondes : QR sur écran ou support imprimé. Réduisez les files grâce à un scan fluide.",
    },
    {
        icon: ShieldCheck,
        title: "Validation en temps réel",
        text: "Chaque lecture est vérifiée côté serveur : vous évitez les faux billets et gardez une source de vérité unique.",
    },
    {
        icon: Ban,
        title: "Anti double entrée",
        text: "Un billet utilisé ne peut pas être réutilisé : les tentatives sont signalées pour sécuriser vos accès. Aucune fraude possible.",
    },
    {
        icon: Ticket,
        title: "Numérique & physique",
        text: "Même logique pour e-tickets, PDF et billets papier avec QR : une politique d’entrée cohérente pour tout le public.",
    },
    {
        icon: Users,
        title: "Plusieurs agents",
        text: "Répartissez le contrôle sur plusieurs entrées ou créneaux : tout reste synchronisé avec votre billetterie Events.",
    },
    {
        icon: BarChart3,
        title: "Pilotage des flux",
        text: "Suivez l’activité aux entrées et appuyez-vous sur les données pour ajuster le dispositif sur le moment ou après coup.",
    },
];

const SCAN_TUTO_REL = "video-Base214586f-Events68d6/Events-scan-tuto.mp4";
const AFFLUENCE_SCREENSHOT = "images/controle-acces-statistiques-affluence.png";

/** Secondes ignorées au début du MP4 (intro trop longue) — ajuster selon le fichier. */
const SCAN_TUTO_SKIP_HEAD_SEC = 7;

const FIABILITE_CAROUSEL_MS = 5500;

const FIABILITE_CAROUSEL_SLIDES = [
    {
        src: "images/verification_acces_concert.jpeg",
        alt: "Verification d'accès au concert",
    },
    {
        src: "images/verification_acces_2_nurigral.jpeg",
        alt: "Verification d'accès à Nurigral",
    },
    {
        src: "images/verification_acces_1_nurigral.jpeg",
        alt: "Verification d'accès à Nurigral",
    },
    {
        src: "images/verification_acces_2_chily.jpeg",
        alt: "Verification d'accès au concert Chily",
    },
    {
        src: "images/verification_acces_chily.jpeg",
        alt: "Verification d'accès au concert Chily",
    },
];

export default function AccessControl() {
    const imagePath = process.env.REACT_APP_IMAGE_PATH;
    const scanTutoVideoRef = useRef(null);
    const canonicalUrl = "https://eventsbilletterie.com/controle-acces";
    const seoTitle = "Controle d'acces evenementiel et scan QR | Events billetterie";
    const seoDescription =
        "Controle d'acces evenementiel avec scan QR en temps reel, anti double entree, mode hors ligne et pilotage des flux pour securiser vos evenements.";
    const seoKeywords =
        "controle d'acces evenement, scan qr code billet, ticket facile, validation billet evenement, anti fraude billet, mode hors ligne scan billet, access control cote d'ivoire, controle d'acces qr code, controle d'acces securise, controle d'acces unique, controle d'acces infalsifiable, controle d'acces qr code unique, controle d'acces qr code securise, controle d'acces qr code infalsifiable, events billetterie, billetterie evenementielle, billetterie en ligne cote d'ivoire, vente de tickets evenement, gestion inscriptions, controle d'acces qr code, billets physiques evenement, bracelets unique, bracelets securises, ticket unique, ticket unique cote d'ivoire, ticket securise, ticket infalsifiable, ticket qr code, bracelet qr code, qr code unique";
    const seoImage = "https://eventsbilletterie.com/assets/images/controle-acces-statistiques-affluence.png";
    const accessControlSeoJsonLd = {
        "@context": "https://schema.org",
        "@type": "Service",
        serviceType: "Controle d'acces evenementiel",
        name: seoTitle,
        description: seoDescription,
        areaServed: {
            "@type": "Country",
            name: "Cote d'Ivoire",
        },
        provider: {
            "@type": "Organization",
            name: "Events Billetterie",
            url: "https://eventsbilletterie.com",
        },
        url: canonicalUrl,
        inLanguage: "fr-CI",
    };

    const scanTutoSrc = `${imagePath}${SCAN_TUTO_REL}`;
    const affluenceShotSrc = `${imagePath}${AFFLUENCE_SCREENSHOT}`;

    const getScanTutoStartTime = useCallback((video) => {
        if (!video || !Number.isFinite(video.duration) || video.duration <= 0) return SCAN_TUTO_SKIP_HEAD_SEC;
        return Math.min(SCAN_TUTO_SKIP_HEAD_SEC, Math.max(0, video.duration - 0.25));
    }, []);

    const seekScanTutoToEssential = useCallback(() => {
        const v = scanTutoVideoRef.current;
        if (!v) return;
        const t = getScanTutoStartTime(v);
        if (Number.isFinite(v.duration)) v.currentTime = t;
    }, [getScanTutoStartTime]);

    const handleScanTutoEnded = useCallback(() => {
        const v = scanTutoVideoRef.current;
        if (!v) return;
        v.currentTime = getScanTutoStartTime(v);
        const p = v.play();
        if (p !== undefined && typeof p.catch === "function") p.catch(() => {});
    }, [getScanTutoStartTime]);

    const carouselLen = FIABILITE_CAROUSEL_SLIDES.length;
    const [carouselIndex, setCarouselIndex] = useState(0);
    const [carouselPaused, setCarouselPaused] = useState(false);

    const carouselGoNext = useCallback(() => {
        setCarouselIndex((i) => (i + 1) % carouselLen);
    }, [carouselLen]);

    const carouselGoPrev = useCallback(() => {
        setCarouselIndex((i) => (i - 1 + carouselLen) % carouselLen);
    }, [carouselLen]);

    useEffect(() => {
        if (carouselPaused || carouselLen < 2) return undefined;
        const id = setInterval(carouselGoNext, FIABILITE_CAROUSEL_MS);
        return () => clearInterval(id);
    }, [carouselPaused, carouselLen, carouselGoNext]);

    return (
        <main className="nc-PageAbout relative overflow-hidden">
            <Helmet>
                <html lang="fr" />
                <title>{seoTitle}</title>
                <meta name="description" content={seoDescription} />
                <meta name="keywords" content={seoKeywords} />
                <meta
                    name="robots"
                    content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"
                />
                <meta property="og:type" content="website" />
                <meta property="og:locale" content="fr_CI" />
                <meta property="og:site_name" content="Events Billetterie" />
                <meta property="og:title" content={seoTitle} />
                <meta property="og:description" content={seoDescription} />
                <meta property="og:url" content={canonicalUrl} />
                <meta property="og:image" content={seoImage} />
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={seoTitle} />
                <meta name="twitter:description" content={seoDescription} />
                <meta name="twitter:image" content={seoImage} />
                <meta name="twitter:site" content="@events_billetterie" />
                <link rel="canonical" href={canonicalUrl} />
                <script type="application/ld+json">
                    {JSON.stringify(accessControlSeoJsonLd)}
                </script>
            </Helmet>

            <div className="nc-BgGlassmorphism pointer-events-none absolute inset-x-0 top-0 min-h-[24rem] pl-20 py-24 flex overflow-hidden z-0">
                <span className="block bg-[#04868b]/20 w-72 h-72 rounded-full mix-blend-multiply filter blur-3xl opacity-30 lg:w-96 lg:h-96" />
                <span className="block bg-primary-6000/20 w-72 h-72 -ml-20 mt-24 rounded-full mix-blend-multiply filter blur-3xl opacity-25 lg:w-96 lg:h-96" />
            </div>

            <div className="container relative z-[1] px-4 pt-12 pb-20 md:pt-16 md:pb-28">
                <section
                    className="max-w-7xl mx-auto py-4 md:py-8"
                    aria-labelledby="heading-hero-controle-acces"
                >
                    {/* Hero type Weezevent : bloc contrasté, texte + visuels — cf. https://weezevent.com/fr/weezaccess/controle-acces/ */}
                    <div className="relative overflow-hidden rounded-[1.75rem] md:rounded-[2rem] bg-gradient-to-br from-[#061a17] via-[#0c302b] to-[#0d9488] px-5 py-10 shadow-[0_28px_64px_-20px_rgba(0,0,0,0.45)] ring-1 ring-white/10 sm:px-8 sm:py-12 md:px-10 md:py-14 lg:px-12 lg:py-16">
                        <div
                            className="pointer-events-none absolute -right-24 -top-24 h-72 w-72 rounded-full bg-emerald-400/15 blur-3xl"
                            aria-hidden
                        />
                        <div className="relative grid gap-10 lg:grid-cols-2 lg:items-center lg:gap-12 xl:gap-14">
                            <div className="min-w-0 text-center text-white lg:text-left">
                                <div className="inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-1.5 text-sm font-medium text-emerald-50/95 backdrop-blur-sm">
                                    <ScanLine className="h-4 w-4" aria-hidden />
                                    Contrôle d&apos;accès
                                </div>
                                <h1
                                    id="heading-hero-controle-acces"
                                    className="mt-5 text-3xl font-bold tracking-tight md:text-4xl xl:text-[2.6rem] xl:leading-[1.12]"
                                >
                                    Assurez un contrôle d&apos;accès{" "}
                                    <span className="text-emerald-300">fluide</span> et{" "}
                                    <span className="text-emerald-300">sécurisé</span>
                                </h1>
                                <p className="mx-auto mt-6 max-w-xl text-lg leading-relaxed text-white/85 md:text-xl lg:mx-0">
                                    Fluidifiez et sécurisez vos entrées avec Events<span className="font-semibold text-emerald-300">Access</span> : la billetterie et la validation sur le
                                    terrain parlent le même langage — scan QR, contrôle en temps réel et files plus courtes,
                                    du plus simple au plus exigeant.
                                </p>
                                <div className="mt-8 flex flex-col items-left justify-left gap-4 sm:flex-row lg:justify-left">
                                    <Link
                                        to='/offre-billetterie'
                                        className="inline-flex items-center justify-center gap-2 rounded-xl bg-white px-8 py-3 text-sm font-semibold text-[#0c302b] shadow-lg shadow-black/20 transition-colors hover:bg-emerald-50 sm:w-auto md:text-base"
                                    >
                                        Voir l&apos;offre billetterie
                                        <ArrowRight className="h-4 w-4" aria-hidden />
                                    </Link>
                                
                                </div>
                            </div>

                            <div className="min-w-0 w-full lg:pl-2">
                                {/* Vidéo en avant-plan haut-gauche ; capture stats en bas-droite avec chevauchement (effet « dashboard + focus ») */}
                                <div className="relative mx-auto flex w-full max-w-xl flex-col items-stretch pb-2 sm:max-w-2xl lg:mx-0 lg:max-w-none">
                                    <div className="relative z-10 w-[min(100%,19.5rem)] shrink-0 sm:w-[min(100%,21rem)]">
                                        <div className="overflow-hidden rounded-2xl bg-black/35 shadow-[0_20px_50px_-12px_rgba(0,0,0,0.5)] ring-1 ring-white/25">
                                            <video
                                                ref={scanTutoVideoRef}
                                                className="block h-auto w-full max-w-full"
                                                autoPlay
                                                muted
                                                playsInline
                                                preload="auto"
                                                onLoadedMetadata={seekScanTutoToEssential}
                                                onLoadedData={seekScanTutoToEssential}
                                                onEnded={handleScanTutoEnded}
                                                aria-label="Démonstration du tutoriel de scan de billets Events"
                                            >
                                                <source src={scanTutoSrc} type="video/mp4" />
                                            </video>
                                        </div>
                                    </div>
                                    <figure className="relative z-20 m-0 -mt-9 w-full max-w-[26rem] self-end sm:-mt-11 sm:max-w-[28rem] md:-mt-14 lg:max-w-[32rem]">
                                        <div className="overflow-hidden rounded-2xl bg-white shadow-[0_24px_56px_-16px_rgba(0,0,0,0.45)] ring-1 ring-white/30">
                                            <img
                                                className="block h-auto w-full max-w-full object-contain object-top"
                                                src={affluenceShotSrc}
                                                alt="Tableau de bord Affluence et contrôle d&apos;entrée : statistiques de scans en temps réel"
                                                loading="eager"
                                                decoding="async"
                                                width={1000}
                                                height={700}
                                            />
                                        </div>
                                    </figure>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                <section className="mt-24 md:mt-32 max-w-6xl mx-auto" aria-labelledby="heading-bracelets-acces">
                    <div className="grid lg:grid-cols-2 gap-10 lg:gap-12 items-center">
                        
                        <div className="min-w-0">
                            <h2
                                id="heading-bracelets-acces"
                                className="text-2xl md:text-3xl font-bold text-neutral-900 dark:text-neutral-50"
                            >
                                Contrôle d'accès simple
                            </h2>
                            <p className="mt-3 text-neutral-600 dark:text-neutral-400 leading-relaxed">
                                Avec Events<span className="font-semibold text-emerald-300">Access</span>, simplifiez la gestion de vos entrées au maximum. 
                                Conçu pour être intuitif et accessible à tous, notre système de contrôle d’accès 
                                ne nécessite aucune formation préalable. 
                                Chaque billet ou badge est muni d’un QR code ou d’un code-barres unique, 
                                permettant une validation instantanée. Rouge c’est non, vert c’est bon !
                            </p>
                            <ul className="mt-6 space-y-3 list-none p-0 m-0">
                                <li className="flex gap-3 text-sm font-semibold text-neutral-700 dark:text-neutral-300">
                                    <Check className="w-5 h-5 text-primary-6000 shrink-0 mt-0.5" aria-hidden />
                                    <span>+300K scans par jour</span>
                                </li>
                                <li className="flex gap-3 text-sm font-semibold text-neutral-700 dark:text-neutral-300">
                                    <Check className="w-5 h-5 text-primary-6000 shrink-0 mt-0.5" aria-hidden />
                                    <span>Pour tous types d’événements</span>
                                </li>
                                <li className="flex gap-3 text-sm font-semibold text-neutral-700 dark:text-neutral-300">
                                    <Check className="w-5 h-5 text-primary-6000 shrink-0 mt-0.5" aria-hidden />
                                    <span>App mobile & terminal pro</span>
                                </li>
                            </ul>
                        </div>
                        <figure className="rounded-3xl bg-white/90 dark:bg-neutral-900/50 p-4 sm:p-5">
                            <img
                            className="max-w-full sm:!max-w-sm h-auto rounded-xl"
                            alt=""
                            loading="lazy"
                            decoding="async"
                            data-nimg={1}
                            style={{ color: "transparent",  }}
                            srcSet={`${imagePath}images/QR-Code_feature-image.webp`}
                            // src={`${imagePath}images/Mobile_pay.png`}
                            />
                        </figure>
                    </div>
                </section>

                {/* Fonctionnalités */}
                <section className="mt-24 md:mt-32 max-w-6xl mx-auto" aria-labelledby="heading-fonctions-acces">
                    <div className="text-center max-w-2xl mx-auto mb-12">
                        <h2
                            id="heading-fonctions-acces"
                            className="text-2xl md:text-3xl font-bold text-neutral-900 dark:text-neutral-50"
                        >
                            Ce que le contrôle d&apos;accès Events<span className="font-semibold text-emerald-300">Access</span> vous apporte
                        </h2>
                        <p className="mt-3 text-neutral-600 dark:text-neutral-400">
                            Une chaîne complète entre la billetterie en ligne et les équipes présentes le jour de
                            l&apos;événement.
                        </p>
                    </div>
                    <ul className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 list-none p-0 m-0">
                        {FEATURES.map(({ icon: Icon, title, text }) => (
                            <li
                                key={title}
                                className="rounded-2xl border border-neutral-200/90 dark:border-neutral-700 bg-white/85 dark:bg-neutral-900/50 p-6 shadow-sm"
                            >
                                <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-gray-600/10 text-gray-700 dark:text-gray-400">
                                    <Icon className="w-5 h-5" strokeWidth={2} aria-hidden />
                                </span>
                                <h3 className="mt-4 text-lg font-semibold text-neutral-900 dark:text-neutral-100">
                                    {title}
                                </h3>
                                <p className="mt-2 text-sm text-neutral-600 dark:text-neutral-400 leading-relaxed">{text}</p>
                            </li>
                        ))}
                    </ul>
                </section>

                {/* Cohérence billetterie */}
                <section
                    className="mt-24 md:mt-32 max-w-6xl mx-auto rounded-3xl border border-neutral-200/90 dark:border-neutral-700 bg-neutral-50/80 dark:bg-neutral-900/40 px-6 py-10 md:px-10 md:py-12"
                    aria-labelledby="heading-ecosysteme-acces"
                >
                    <div className="flex flex-col md:flex-row md:items-start gap-6 md:gap-10">
                        <div className="min-w-0">
                            <h2
                                id="heading-ecosysteme-acces"
                                className="text-2xl md:text-3xl font-bold text-neutral-900 dark:text-neutral-50"
                            >
                                Tu n'as pas encore compris ?
                            </h2>
                            <p className="mt-3 text-neutral-600 dark:text-neutral-400 leading-relaxed">
                                Nous sommes votre partenaire privilegié de la billetterie événementielle. Confiez-nous l&rsquo;intégralité de votre billetterie : vente en ligne, impression sécurisée de billets, bracelets électroniques infalsifiables et contrôle d&rsquo;accès le jour J. Notre équipe s&rsquo;occupe de tout, pour que vous puissiez organiser votre événement en toute sérénité et avec une confiance absolue.
                            </p>
                       
                            <div className="mt-6 flex flex-wrap gap-3">
                                <Link
                                    to="/nous-contacter"
                                    className="inline-flex items-center gap-2 text-sm font-medium text-blue-600 hover:underline"
                                >
                                    Discutons de votre prochain événement
                                </Link>OU
                                <Link
                                    to="/offre-billetterie"
                                    className="inline-flex items-center gap-2 text-sm font-medium text-blue-600 hover:underline"
                                >
                                    Voir l'offre billetterie
                                </Link>
                            </div>
                        </div>
                    </div>
                </section>

                <div className="mt-24 md:mt-32 max-w-6xl mx-auto container px-4 sm:px-6 lg:px-8 relative z-10">

                    <div
                        className="relative py-10 glass-card rounded-3xl"
                        aria-labelledby="heading-fiabilite-hors-ligne"
                    >
                        <div className="relative flex flex-col items-center sm:max-w-4xl mx-auto">
                            <div className="flex flex-col items-center text-center px-4">
                                <span
                                    className="mb-5 inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-gray-600/15 text-gray-700 dark:bg-gray-600/20 dark:text-gray-400"
                                    aria-hidden
                                >
                                    <WifiOff className="h-7 w-7" strokeWidth={1.75} />
                                </span>
                                <h2
                                    id="heading-fiabilite-hors-ligne"
                                    className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-neutral-50"
                                >
                                    Fiable, même hors ligne.
                                </h2>
                                <p className="mt-5 text-base font-normal leading-relaxed text-gray-800 dark:text-neutral-300 md:text-lg">
                                    Avec Events<span className="font-semibold text-emerald-300">Access</span>, vous pouvez continuer à valider les billets même en cas de coupure réseau. 
                                    Les données utiles au scan sont conservées localement sur chaque terminal, 
                                    permettant aux équipes de poursuivre la validation des billets pendant une baisse ou une coupure du réseau, 
                                    pour des entrées plus sereines le jour J.
                                </p>
                            </div>
                        </div>

                        <div className="flex justify-center items-center">

                            <a href='/dashboard/events/registration' className="nc-Button relative h-auto inline-flex items-center justify-center rounded-lg transition-colors text-sm sm:text-sm font-medium px-4 py-2 sm:px-4 ttnc-ButtonPrimary bg-primary-6000 hover:bg-primary-700 text-white mt-6 sm:mt-6">
                                Ouvrir ma billetterie gratuitement
                            </a>
                        </div>
                    </div>
                </div>

                {carouselLen > 0 && (
                    <div
                    className="relative mx-auto mt-10 w-full max-w-3xl px-4"
                    onMouseEnter={() => setCarouselPaused(true)}
                    onMouseLeave={() => setCarouselPaused(false)}
                    onFocus={() => setCarouselPaused(true)}
                    onBlur={(e) => {
                        if (!e.currentTarget.contains(e.relatedTarget)) setCarouselPaused(false);
                    }}
                    role="region"
                    aria-roledescription="carrousel"
                    aria-label="Aperçus contrôle d&apos;accès et billetterie"
                    >
                    <div className="overflow-hidden rounded-2xl border border-neutral-200/80 bg-neutral-100/80 shadow-inner dark:border-neutral-600/80 dark:bg-neutral-800/50">
                    <div
                    className="flex transition-transform duration-500 ease-out motion-reduce:transition-none"
                    style={{ transform: `translateX(-${carouselIndex * 100}%)` }}
                    >
                    {FIABILITE_CAROUSEL_SLIDES.map((slide, slideIndex) => (
                        <div
                        key={slide.src}
                        className="flex min-w-full shrink-0 items-center justify-center p-3 sm:p-5"
                        >
                        <img
                        className="max-h-[min(55vh,22rem)] w-full object-contain object-center"
                        src={`${imagePath}${slide.src}`}
                        alt={slide.alt}
                        loading={slideIndex === carouselIndex ? "eager" : "lazy"}
                        decoding="async"
                        />
                        </div>
                    ))}
                    </div>
                    </div>
                    {carouselLen > 1 && (
                        <>
                        <button
                        type="button"
                        className="absolute left-1 top-1/2 z-10 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border border-neutral-200 bg-white/95 text-neutral-800 shadow-md transition hover:bg-white dark:border-neutral-600 dark:bg-neutral-800/95 dark:text-neutral-100 dark:hover:bg-neutral-800 sm:left-2"
                        onClick={carouselGoPrev}
                        aria-label="Image précédente"
                        >
                        <ChevronLeft className="h-5 w-5" aria-hidden />
                        </button>
                        <button
                        type="button"
                        className="absolute right-1 top-1/2 z-10 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border border-neutral-200 bg-white/95 text-neutral-800 shadow-md transition hover:bg-white dark:border-neutral-600 dark:bg-neutral-800/95 dark:text-neutral-100 dark:hover:bg-neutral-800 sm:right-2"
                        onClick={carouselGoNext}
                        aria-label="Image suivante"
                        >
                        <ChevronRight className="h-5 w-5" aria-hidden />
                        </button>
                        <div
                        className="mt-4 flex justify-center gap-2"
                        role="tablist"
                        aria-label="Choisir une image"
                        >
                        {FIABILITE_CAROUSEL_SLIDES.map((slide, i) => (
                            <button
                            key={slide.src}
                            type="button"
                            role="tab"
                            aria-selected={i === carouselIndex}
                            aria-label={`Image ${i + 1} sur ${carouselLen}`}
                            className={`h-2.5 w-2.5 rounded-full transition ${
                                i === carouselIndex
                                ? "bg-primary-6000 scale-110"
                                : "bg-neutral-300 hover:bg-neutral-400 dark:bg-neutral-600 dark:hover:bg-neutral-500"
                            }`}
                            onClick={() => setCarouselIndex(i)}
                            />
                        ))}
                        </div>
                        </>
                    )}
                    </div>
                )}

                {/* CTA */}
                <div className="mt-24 max-w-3xl mx-auto text-center rounded-3xl bg-primary-6000 text-white px-8 py-12 md:py-14">
                    <h2 className="text-4xl md:text-5xl font-bold">Prêt à optimiser votre contrôle d&apos;accès ?</h2>
                    
                    <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4">
                        <a
                            href='/dashboard/events/registration'
                            className="inline-flex items-center justify-center gap-2 rounded-full bg-white text-primary-6000 font-semibold px-8 py-2.5 text-sm hover:bg-neutral-100 transition-colors w-full sm:w-auto"
                        >
                            Créer ma billetterie
                            <ArrowRight className="w-4 h-4" aria-hidden />
                        </a>
                        <Link
                            to="/nous-contacter"
                            className="inline-flex items-center justify-center rounded-full text-white font-semibold px-6 py-2.5 text-sm hover:bg-white/10 w-full sm:w-auto"
                        >
                            Nous contacter
                        </Link>
                    </div>
                </div>
            </div>
        </main>
    );
}
